import { Button, Input, Modal, SearchBar } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { cms } from "../../api/cms.service";
import type { CmsPage, CmsMenu, ImportResult } from "../../types/cms";
import { PageHeader } from "../../components/PageHeader";
import LoadingState from "../../components/LoadingState";
import EmptyState from "../../components/EmptyState";
import { formatDateTimeShort } from "../../utils/dateFormatter";
import { ROUTES } from "../../routes";
import { usePermissions } from "../../hooks/usePermissions";
import { pagePath } from "./pageAddress";
import { isPageSection, PAGE_SECTIONS, pageSectionLabel, pageSectionOf } from "./pageSection";
import { PagesImportModal } from "./components/PagesImportModal/PagesImportModal";
import { FRAGMENT_PAGE_TYPE, getPageType } from "./pageTypeRegistry";
import styles from "./PagesListPage.module.css";
import cmsStyles from "./CMS.module.css";

function getStatusBadgeClass(status: string): string {
  switch (status) {
    case "published":
      return cmsStyles.statusPublished;
    case "archived":
      return cmsStyles.statusArchived;
    default:
      return cmsStyles.statusDraft;
  }
}

type PageGroup = {
  menu: CmsMenu | null;
  pages: CmsPage[];
};

function groupPagesByMenu(pages: CmsPage[], menus: CmsMenu[]): PageGroup[] {
  const menuMap = new Map<string, CmsMenu>();
  for (const menu of menus) {
    menuMap.set(menu.id, menu);
  }

  const grouped = new Map<string | null, CmsPage[]>();

  const pagesByAddress = [...pages].sort((a, b) => pagePath(a).localeCompare(pagePath(b)));

  for (const page of pagesByAddress) {
    const key = page.menuId ?? null;
    const existing = grouped.get(key) ?? [];
    existing.push(page);
    grouped.set(key, existing);
  }

  const groups: PageGroup[] = [];

  // Menu groups first
  for (const menu of menus) {
    const menuPages = grouped.get(menu.id);
    if (menuPages && menuPages.length > 0) {
      groups.push({ menu, pages: menuPages });
    }
  }

  // Ungrouped pages last
  const ungrouped = grouped.get(null);
  if (ungrouped && ungrouped.length > 0) {
    groups.push({ menu: null, pages: ungrouped });
  }

  return groups;
}

function PageRow({ page, onClick }: { page: CmsPage; onClick: () => void }) {
  return (
    <div
      className={styles.pageRow}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === "Enter" && onClick()}
    >
      <div className={styles.pageInfo}>
        <span className={styles.pageTitle}>{page.title}</span>
        <span className={styles.pageSlug}>{pagePath(page)}</span>
      </div>
      <div className={styles.pageMeta}>
        <span className={styles.pageSection}>{pageSectionLabel(pageSectionOf(page))}</span>
        <span className={styles.pageType}>{getPageType(page.type)?.name ?? page.type}</span>
        <span className={`${cmsStyles.statusBadge} ${getStatusBadgeClass(page.status)}`}>
          {page.status}
        </span>
        <span className={styles.pageDate}>{formatDateTimeShort(page.updatedAt)}</span>
      </div>
    </div>
  );
}

type ImportFormValues = {
  restApiKey: string;
};

type ImportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

function ImportLegacyModal({ isOpen, onClose, onSuccess }: ImportModalProps) {
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useForm<ImportFormValues>({
    defaultValues: {
      restApiKey: "",
    },
  });

  function handleClose() {
    reset();
    setImportResult(null);
    setImportError(null);
    onClose();
  }

  async function onSubmit(values: ImportFormValues) {
    setImportResult(null);
    setImportError(null);
    try {
      const result = await cms.importLegacy(values.restApiKey);
      setImportResult(result);
      onSuccess();
    } catch (err) {
      setImportError(err instanceof Error ? err.message : "Import failed");
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import Legacy Data" size="medium">
      <form onSubmit={handleSubmit(onSubmit)} className={styles.importForm}>
        <p style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)" }}>
          The legacy REST API URL is configured server-side. Enter the API key to authenticate.
        </p>
        <div className={styles.importField}>
          <Input
            label="REST API Key"
            type="password"
            {...register("restApiKey")}
            placeholder="Enter API key"
          />
        </div>

        {importError && (
          <div className={styles.importError} role="alert">
            {importError}
          </div>
        )}

        {importResult && (
          <div className={styles.importResults} role="status">
            <p className={styles.importResultsTitle}>Import complete</p>
            <table className={styles.importResultsTable}>
              <thead>
                <tr>
                  <th>Resource</th>
                  <th>Imported</th>
                  <th>Failed</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Pages</td>
                  <td>{importResult.pages.imported}</td>
                  <td>{importResult.pages.failed}</td>
                </tr>
                <tr>
                  <td>Blocks</td>
                  <td>{importResult.blocks.imported}</td>
                  <td>{importResult.blocks.failed}</td>
                </tr>
                <tr>
                  <td>Menus</td>
                  <td>{importResult.menus.imported}</td>
                  <td>{importResult.menus.failed}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className={styles.importActions}>
          <Button variant="outlined" onClick={handleClose} type="button">
            {importResult ? "Close" : "Cancel"}
          </Button>
          {!importResult && (
            <Button color="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Importing..." : "Import"}
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
}

function PagesListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pages, setPages] = useState<CmsPage[]>([]);
  const [menus, setMenus] = useState<CmsMenu[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [pagesImportOpen, setPagesImportOpen] = useState(false);
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission("cms.write");
  const requestedSection = searchParams.get("section");
  const sectionFilter = isPageSection(requestedSection) ? requestedSection : undefined;
  const newPageRoute = sectionFilter
    ? `${ROUTES.cms.pageNew}?${new URLSearchParams({ section: sectionFilter })}`
    : ROUTES.cms.pageNew;

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleSearchChange(value: string) {
    setSearchTerm(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      const newParams = new URLSearchParams(searchParams);
      if (value) {
        newParams.set("search", value);
      } else {
        newParams.delete("search");
      }
      setSearchParams(newParams);
    }, 300);
  }

  function handleSectionChange(value: string) {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set("section", value);
    } else {
      newParams.delete("section");
    }
    setSearchParams(newParams);
  }

  async function loadData() {
    try {
      setLoading(true);
      setError(null);
      const [pagesResponse, menusResponse] = await Promise.all([
        cms.getPages({ search: searchTerm || undefined, section: sectionFilter }),
        cms.getMenus({ parentId: null }),
      ]);
      setPages(pagesResponse.pages.filter(page => page.type !== FRAGMENT_PAGE_TYPE));
      setMenus(menusResponse.menus);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load pages"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, [searchParams.get("search"), sectionFilter]);

  const groups = groupPagesByMenu(pages, menus);

  return (
    <div className={cmsStyles.wrapper}>
      <PageHeader title="CMS Pages" subtitle="Manage CMS pages and their content">
        {canWrite && (
          <>
            <Button variant="outlined" onClick={() => setImportModalOpen(true)}>
              Import Legacy Data
            </Button>
            <Button variant="outlined" onClick={() => setPagesImportOpen(true)}>
              Import pages
            </Button>
            <Button variant="contained" color="primary" onClick={() => navigate(newPageRoute)}>
              New Page
            </Button>
          </>
        )}
      </PageHeader>

      <ImportLegacyModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={loadData}
      />

      <PagesImportModal
        isOpen={pagesImportOpen}
        onClose={() => setPagesImportOpen(false)}
        onImported={loadData}
      />

      <div className={styles.filters}>
        <div className={cmsStyles.searchSection}>
          <SearchBar
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search pages by title or slug..."
            onClear={() => handleSearchChange("")}
          />
        </div>
        <div className={styles.sectionFilter}>
          <label className={styles.sectionFilterLabel} htmlFor="pages-section-filter">
            Section
          </label>
          <select
            id="pages-section-filter"
            className={cmsStyles.selectField}
            value={sectionFilter ?? ""}
            onChange={event => handleSectionChange(event.target.value)}
          >
            <option value="">All sections</option>
            {PAGE_SECTIONS.map(section => (
              <option key={section.value} value={section.value}>
                {section.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={cmsStyles.contentSection}>
        {loading ? (
          <LoadingState message="Loading pages..." />
        ) : error ? (
          <EmptyState
            variant="error"
            title="Failed to load pages"
            message={error.message}
            action={{ label: "Retry", onClick: loadData }}
          />
        ) : pages.length === 0 ? (
          <EmptyState
            variant="empty"
            title="No pages yet"
            message="Create your first CMS page to get started."
            action={{ label: "New Page", onClick: () => navigate(newPageRoute) }}
          />
        ) : (
          <div className={styles.groupList}>
            {groups.map(group => (
              <div key={group.menu?.id ?? "ungrouped"} className={styles.group}>
                <div className={styles.groupHeader}>
                  <div className={styles.groupInfo}>
                    <span className={styles.groupName}>
                      {group.menu ? (group.menu.name ?? group.menu.title) : "Ungrouped Pages"}
                    </span>
                    {group.menu?.description && (
                      <span className={styles.groupDescription}>{group.menu.description}</span>
                    )}
                    {!group.menu && (
                      <span className={styles.groupDescription}>
                        Pages not assigned to any menu
                      </span>
                    )}
                  </div>
                  <span className={styles.groupCount}>
                    {group.pages.length} page{group.pages.length !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className={styles.pageList}>
                  {group.pages.map(page => (
                    <PageRow
                      key={page.id}
                      page={page}
                      onClick={() => navigate(ROUTES.cms.pageDetail(page.id))}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default PagesListPage;
